import { isLocalThreadId } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { useSyncExternalStore } from 'react';
export const EMPTY_LOCAL_THREAD_ROWS = { items: [], pinnedItems: [] };
function createExternalValue(initial) {
    let value = initial;
    const listeners = new Set();
    const get = () => value;
    const subscribe = (listener) => {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    };
    return {
        get,
        set: (next) => {
            if (value === next)
                return;
            value = next;
            for (const listener of listeners) {
                listener();
            }
        },
        subscribe,
        use: () => useSyncExternalStore(subscribe, get, get),
    };
}
const rows = createExternalValue(EMPTY_LOCAL_THREAD_ROWS);
export const setLocalThreadRows = rows.set;
export const getLocalThreadRows = rows.get;
export const useLocalThreadRows = rows.use;
/**
 * Whether the running shell can surface local threads at all (Local mode is
 * reachable on this install, even with zero rows yet). Gates local-only UI
 * such as the Library's Local type filter. Web never publishes, so it stays
 * `false` there.
 */
const available = createExternalValue(false);
export const setLocalThreadsAvailable = available.set;
export const useLocalThreadsAvailable = available.use;
const manageable = createExternalValue(true);
export const setLocalThreadsManageable = manageable.set;
export const useLocalThreadsManageable = manageable.use;
/** Rename, pin, delete, and read-state changes for these threads: cloud threads always; local threads only while the desktop shell reports Local mode able to serve them. */
export function useCanManageThreads(threadIds) {
    const localManageable = useLocalThreadsManageable();
    return localManageable || threadIds.every((id) => !id || !isLocalThreadId(id));
}
/**
 * Title-substring match for a user search term — local sessions have no
 * content search index, so every surface filters them the same way. Pass the
 * output of `normalizeLocalSearchTerm`; `null` matches everything.
 */
export function matchesLocalThreadTitle(row, normalizedTerm) {
    return normalizedTerm === null || (row.title ?? '').toLowerCase().includes(normalizedTerm);
}
export function normalizeLocalSearchTerm(term) {
    return term?.trim().toLowerCase() || null;
}
/**
 * Recent and pinned rows as one deduplicated list: the recent page may
 * include pinned rows, and pins beyond the loaded page appear only in
 * `pinnedItems`.
 */
export function allLocalThreadRows(source) {
    if (source.pinnedItems.length === 0) {
        return source.items;
    }
    const seen = new Set(source.items.map((row) => row.context_uuid).filter(Boolean));
    return [...source.items, ...source.pinnedItems.filter((row) => !seen.has(row.context_uuid))];
}
function rowsEqual(a, b) {
    return (a.length === b.length &&
        a.every((row, index) => {
            const other = b[index];
            return (other !== undefined &&
                row.context_uuid === other.context_uuid &&
                row.title === other.title &&
                row.last_query_datetime === other.last_query_datetime &&
                row.thread_status === other.thread_status &&
                row.unread === other.unread &&
                row.is_pinned === other.is_pinned &&
                row.wake_at === other.wake_at);
        }));
}
/** Bridge-side publish damper: native snapshots re-serialize on every cloud
 * change, so reference inequality alone would fan out no-op re-merges. */
export function areLocalThreadRowsEqual(a, b) {
    return rowsEqual(a.items, b.items) && rowsEqual(a.pinnedItems, b.pinnedItems);
}
//# sourceMappingURL=localThreadRows.js.map